import {
  RegroupementFraisNature,
  type DossierDTO,
  type TypeFacturation,
  type FactureDTO,
  type RemboursementDTO
} from '@/features/api/extranet/dto/Dossier/Synthese'
import  { StatutFactureBadgeType, type StatutFactureDTO, StatutFactureLibelle } from '@/features/api/extranet/dto/Facture/StatutFactureDTO.ts'
import { Statut as StatutRemboursement, StatutBadgeType as StatutRemboursementBadgeType, StatutLibelle as StatutRemboursementLibelle } from '@/features/domaine/types/opco/Remboursements'
import { computed, inject } from 'vue'
import { DEMANDE_STORE } from '@/features/interface/demande/shared'
import { storeToRefs } from 'pinia'
import { isNotNullUndefinedOrEmptyString, isNumber } from '@/features/shared/tools'
import type { BadgeType } from '@designSystem/components'

export type RegroupementFrais = Record<RegroupementFraisNature, number | null>

export type SyntheseItem = {
  lettre: 'A' | 'B' | 'C' | 'E' | 'F'
  libelle: string
  frais: RegroupementFrais
}
type FactureItemType = 'facture' | 'remboursement'
export type FactureItem = (
  | {
      origine: Extract<FactureItemType, 'facture'>
      statut: StatutFactureDTO
    }
  | {
      origine: Extract<FactureItemType, 'remboursement'>
      statut: StatutRemboursement
    }
) & {
  type: TypeFacturation
  reference: string
  dateEmission: string
  frais: RegroupementFrais
  statutLibelle: string
  statutBadge: BadgeType
}

type SyntheseFacture = {
  lettre: 'D'
  libelle: string,
  factures: FactureItem[]
}

export type SyntheseReglements = {
  dossier: string
  a: SyntheseItem
  b: SyntheseItem
  c: SyntheseItem
  d: SyntheseFacture
  e: SyntheseItem
  f: SyntheseItem
}

const EMPTY_REGROUPEMENT_FRAIS: RegroupementFrais = {
  Pedagogique: null,
  Remuneration: null,
  Annexe: null,
  Divers: null,
} as const

const FILL_ZERO_REGROUPEMENT_FRAIS: RegroupementFrais = {
  Pedagogique: 0,
  Remuneration: 0,
  Annexe: 0,
  Divers: 0,
} as const

function getStatutInfos(f: RemboursementDTO | FactureDTO) : Pick<FactureItem, 'statutBadge' | 'statutLibelle'> {
  const estRemboursement = 'referenceProforma' in f
  if (estRemboursement)
    return {
      statutBadge: StatutRemboursementBadgeType[f.statut],
      statutLibelle: StatutRemboursementLibelle[f.statut],
    }
  else
    return {
      statutBadge: StatutFactureBadgeType[f.statut],
      statutLibelle: StatutFactureLibelle[f.statut],
    }
}

export const useSyntheseReglements = () => {
  const store = inject(DEMANDE_STORE)!.getStore<'MODEL_DR'>()
  const { model } = storeToRefs(store)

  function calculerFacturation(dossier: DossierDTO, key: 'factures' | 'remboursements'): FactureItem[] {
    return dossier[key].flatMap((f) =>
      f.facturation
        .map((facItem) => ({
          origine: key === 'factures' ? 'facture' : 'remboursement',
          type: f.type,
          statut: f.statut,
          reference: ('referenceProforma' in f ? [f.reference, f.referenceProforma] : [f.reference])
            .filter(isNotNullUndefinedOrEmptyString)
            .join(' / '),
          dateEmission: f.dateEmission!.split('T')[0],
          regroupement: facItem.regroupementFraisNature!,
          montant: facItem.montantFactureHt ?? 0,
          ...getStatutInfos(f),
        }))
        .reduce((acc, ligne) => {
          const facturationItem =
            acc.find((item) => item.reference === ligne.reference) ??
            (() => {
              const newItem = {
                ...ligne,
                frais: { ...EMPTY_REGROUPEMENT_FRAIS },
              } as FactureItem
              acc.push(newItem)
              return newItem
            })()

          if (isNumber(ligne.montant)) {
            facturationItem.frais[ligne.regroupement] = (facturationItem.frais[ligne.regroupement] ?? 0) + ligne.montant
          }
          return acc
        }, [] as FactureItem[]),
    )
  }

  function calculerA(dossier: DossierDTO): SyntheseItem {
    const frais = (dossier.financements ?? [])
      .filter((f) => f.typeMontantDemande === 'Monetaire')
      .flatMap((f) => ({ nature: f.regroupementFraisNature, montant: f.montantDemande }))
      .reduce(
        (synthese, frais) => {
          if (isNumber(frais.montant)) {
            synthese[frais.nature] = (synthese[frais.nature] ?? 0) + frais.montant
          }
          return synthese
        }, { ...EMPTY_REGROUPEMENT_FRAIS },
      )

    const libelle = 'Montants demandés'
    return { lettre: 'A', libelle, frais }
  }

  function calculerB(dossier: DossierDTO): SyntheseItem {
    const frais = (dossier.financements ?? [])
      .flatMap((f) =>
        f.financementDetails.map((d) => ({ nature: f.regroupementFraisNature, montant: d.montantEngage })),
      )
      .reduce(
        (synthese, frais) => {
          synthese[frais.nature] = (synthese[frais.nature] ?? 0) + (frais.montant ?? 0)
          return synthese
        }, { ...FILL_ZERO_REGROUPEMENT_FRAIS },
      )

    const libelle = 'Montants accordés'
    return { lettre: 'B', libelle, frais }
  }

  function calculerC(demandé: SyntheseItem, engagé: SyntheseItem): SyntheseItem {
    const frais = Object.fromEntries(
      RegroupementFraisNature.map(
        (key) =>
          isNumber(demandé.frais[key]) ?
            [key, Math.max(0, demandé.frais[key] - engagé.frais[key]!)] :
            [key, null],
      ),
    ) as RegroupementFrais
    const libelle = 'Reste à charge (A - B)'
    return { lettre: 'C', libelle, frais }
  }

  function calculerD(dossier: DossierDTO): SyntheseFacture {
    const frais = [
      ...calculerFacturation(dossier, 'factures'),
      ...calculerFacturation(dossier, 'remboursements'),
    ].sort((a, b) => a.dateEmission > b.dateEmission ? 1 : -1)

    const libelle = 'Factures transmises'
    return { lettre: 'D', libelle, factures: frais }
  }

  function calculerE(syntheseFacture: SyntheseFacture): SyntheseItem {
    const frais = syntheseFacture.factures.reduce(
      (total, facture) => {
        RegroupementFraisNature
          .filter((key) => isNumber(facture.frais[key]))
          .forEach((key) => {
            total[key] = (total[key] ?? 0) + facture.frais[key]!
        })
        return total
      },
      { ...EMPTY_REGROUPEMENT_FRAIS },
    )
    const libelle = 'Sous total (somme de D)'
    return { lettre: 'E', libelle, frais }
  }

  function calculerF(b: SyntheseItem, e: SyntheseItem): SyntheseItem {
    const libelle = 'Solde à facturer (B - E si positif ou 0)'
    const frais = {
      Pedagogique: Math.max(0, b.frais.Pedagogique! - e.frais.Pedagogique!),
      Remuneration: Math.max(0, b.frais.Remuneration! - e.frais.Remuneration!),
      Annexe: Math.max(0, b.frais.Annexe! - e.frais.Annexe!),
      Divers: Math.max(0, b.frais.Divers! - e.frais.Divers!),
    }
    return { lettre: 'F', libelle, frais }
  }

  const data = computed<SyntheseReglements>(() => {
    const dossier = model.value!.dossier?.code ?? 'Inconnu'
    const a = calculerA(model.value!.dossier!)
    const b = calculerB(model.value!.dossier!)
    const c = calculerC(a, b)
    const d = calculerD(model.value!.dossier!)
    const e = calculerE(d)
    const f = calculerF(b, e)

    return { dossier, a, b, c, d, e, f } as SyntheseReglements
  })

  return { data }
}
